/** The keypad: 10-key layout, inner lines only, 0 in its own box below. Even tables show even keys only. */
import { KEYPAD_LAYOUT, ROUTES, keyShows } from "../engine/curriculum";
import type { Puzzle } from "../engine/puzzle";

export type KeypadMode = "live" | "static" | "build";

export interface Guide {
  /** Circle this key (the counting start). */
  start?: number;
  /** Draw the whole direction of counting. "back" reverses the arrows. */
  arrows?: "forward" | "back";
  /** Fill the anchor key, mark the answer key with ?, and draw arrows from one to the other. */
  puzzle?: Puzzle;
  /** Review counts: the anchors stay visible (gold, labeled) … */
  anchors?: { key: number; value: number }[];
  /** … and the key being counted right now is outlined. */
  active?: number;
}

const kpos = (d: number) => {
  if (d === 0) return { x: 87, y: 213 };
  const i = KEYPAD_LAYOUT.indexOf(d as (typeof KEYPAD_LAYOUT)[number]);
  return { x: (i % 3) * 58 + 29, y: Math.floor(i / 3) * 58 + 29 };
};

const arrow = (d1: number, d2: number, color: string, dash: boolean, width = 3, pad = 20, endPad = 24) => {
  const p = kpos(d1), q = kpos(d2), dx = q.x - p.x, dy = q.y - p.y, L = Math.hypot(dx, dy);
  const x1 = p.x + (dx * pad) / L, y1 = p.y + (dy * pad) / L, x2 = q.x - (dx * endPad) / L, y2 = q.y - (dy * endPad) / L;
  const id = color === "var(--navy)" ? "ahN" : color === LIGHT ? "ahL" : dash ? "ahG" : "ahO";
  return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${color}" stroke-width="${width}" ${dash ? 'stroke-dasharray="4 4"' : ""} marker-end="url(#${id})"/>`;
};

/** Route arrows fade to light gray when a puzzle is drawn on top: the dark color belongs to the anchor and the route to the answer (Meliesa, Oct 29). */
const LIGHT = "#C7D0DA";

export function guideSVG(table: number, g?: Guide): string {
  if (!g) return "";
  let s = "";
  const R = ROUTES[table];
  if (g.arrows && R) {
    const runs = g.arrows === "back" ? [...R].reverse().map((r) => [...r].reverse()) : R;
    const quiet = !!(g.puzzle && g.puzzle.keys.length);
    runs.forEach((run, ri) => {
      for (let k = 1; k < run.length; k++) s += arrow(run[k - 1]!, run[k]!, quiet ? LIGHT : "var(--orange)", false, quiet ? 2.5 : 3);
      if (ri < runs.length - 1) s += arrow(run[run.length - 1]!, runs[ri + 1]![0]!, quiet ? LIGHT : "var(--gold)", true, quiet ? 2.5 : 3);
    });
  }
  if (g.puzzle && g.puzzle.keys.length) {
    const P = g.puzzle;
    const a = kpos(P.keys[0]!);
    s += `<circle cx="${a.x}" cy="${a.y}" r="25" fill="var(--gold)" opacity=".28"/>`;
    s += `<text x="${a.x - 26}" y="${a.y - 15}" text-anchor="start" font-size="10" font-weight="700" fill="var(--gold)">${P.anchorValue}</text>`;
    for (let k = 1; k < P.keys.length; k++) s += arrow(P.keys[k - 1]!, P.keys[k]!, "var(--navy)", false, 3.5, 27, k === P.keys.length - 1 ? 31 : 27);
    if (P.answerKey !== null && P.keys.length > 1) {
      const z = kpos(P.answerKey);
      s += `<rect x="${z.x - 24}" y="${z.y - 24}" width="48" height="48" rx="8" fill="none" stroke="var(--navy)" stroke-width="3" stroke-dasharray="5 4"/>`;
      s += `<text x="${z.x + 19}" y="${z.y - 13}" text-anchor="middle" font-size="12" font-weight="700" fill="var(--navy)">?</text>`;
    }
  }
  if (g.anchors) for (const an of g.anchors) {
    const a = kpos(an.key);
    s += `<circle cx="${a.x}" cy="${a.y}" r="25" fill="var(--gold)" opacity=".28"/>`;
    s += `<text x="${a.x - 26}" y="${a.y - 15}" text-anchor="start" font-size="10" font-weight="700" fill="var(--gold)">${an.value}</text>`;
  }
  if (g.active !== undefined) {
    const z = kpos(g.active);
    s += `<rect x="${z.x - 24}" y="${z.y - 24}" width="48" height="48" rx="8" fill="none" stroke="var(--navy)" stroke-width="3" stroke-dasharray="5 4"/>`;
    s += `<text x="${z.x + 19}" y="${z.y - 13}" text-anchor="middle" font-size="12" font-weight="700" fill="var(--navy)">?</text>`;
  }
  if (g.start !== undefined) {
    const c = kpos(g.start);
    s += `<circle cx="${c.x}" cy="${c.y}" r="25" fill="none" stroke="var(--orange)" stroke-width="3"/>`;
  }
  if (!s) return "";
  const m = (id: string, fill: string) => `<marker id="${id}" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="4" markerHeight="4" orient="auto"><path d="M0,0 L10,5 L0,10 z" fill="${fill}"/></marker>`;
  return `<svg class="guide" viewBox="0 0 174 242" aria-hidden="true"><defs>${m("ahO", "var(--orange)")}${m("ahG", "var(--gold)")}${m("ahN", "var(--navy)")}${m("ahL", LIGHT)}</defs>${s}</svg>`;
}

export function keypadHTML(table: number, mode: KeypadMode, guide?: Guide): string {
  let h = `<div class="kpwrap">${guideSVG(table, guide)}<div class="kp">`;
  KEYPAD_LAYOUT.forEach((d, i) => {
    const cls = "kc" + (i % 3 < 2 ? " r" : "") + (i < 6 ? " b" : "");
    if (mode === "build") h += `<div class="${cls}"><input data-d="${d}" inputmode="numeric" maxlength="2" aria-label="Keypad space ${i + 1}"></div>`;
    else if (!keyShows(table, d)) h += `<div class="${cls}" aria-hidden="true"></div>`;
    else if (mode === "live") h += `<button type="button" class="${cls}" data-d="${d}" aria-label="Key ${d}"><span class="d">${d}</span></button>`;
    else h += `<div class="${cls}"><span class="d">${d}</span></div>`;
  });
  h += `</div>`;
  if (mode === "build") h += `<div class="kc kzero" style="display:grid"><input data-d="0" inputmode="numeric" maxlength="2" aria-label="Keypad space below the grid"></div>`;
  else if (mode === "live") h += `<button type="button" class="kc kzero" data-d="0" aria-label="Key 0"><span class="d">0</span></button>`;
  else h += `<div class="kc kzero"><span class="d">0</span></div>`;
  return h + `</div>`;
}

/** Stage 1: tap keys to follow along; tapping highlights the key, tapping again clears it. No numbers. */
/**
 * Division, Stage 1: every key you touch is one count. Each tap writes its count on the key (a key tapped twice
 * on an even table shows both), and the running count is reported so the page can show "7 touches".
 */
export function wireCounting(box: HTMLElement, onCount?: (n: number) => void): void {
  let n = 0;
  box.querySelectorAll<HTMLButtonElement>("button.kc").forEach((btn) => {
    btn.onclick = () => {
      n++;
      btn.classList.add("mark");
      const o = document.createElement("span"); o.className = "ord"; o.textContent = btn.querySelector(".ord") ? `${btn.querySelector(".ord")!.textContent},${n}` : String(n);
      btn.querySelector(".ord")?.remove(); btn.appendChild(o);
      onCount?.(n);
    };
  });
  const clr = box.querySelector<HTMLButtonElement>("[data-clear]");
  if (clr) clr.onclick = () => { n = 0; box.querySelectorAll(".mark").forEach((b) => b.classList.remove("mark")); box.querySelectorAll(".ord").forEach((o) => o.remove()); onCount?.(0); };
}

export function wireMarking(box: HTMLElement, onMark?: () => void): void {
  box.querySelectorAll<HTMLButtonElement>("button.kc").forEach((btn) => {
    btn.onclick = () => {
      btn.classList.toggle("mark");
      onMark?.();
    };
  });
  const clr = box.querySelector<HTMLButtonElement>("[data-clear]");
  if (clr) clr.onclick = () => box.querySelectorAll(".mark").forEach((b) => b.classList.remove("mark"));
}

/** The order the student writes the keys in when building: 1 to 9 (even tables: 2, 4, 6, 8), then 0. */
export const buildOrder = (table: number): number[] => [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((d) => keyShows(table, d)).concat(0);

/** Render bold markers from engine text. */
export const rich = (s: string): string => s.replace(/\*\*(.+?)\*\*/g, "<b>$1</b>");

export const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
